import { useState } from 'react';
import { Loader2, Play, RefreshCw, CheckCircle2, AlertTriangle, XCircle, ShieldAlert, HelpCircle, WifiOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtDate(iso) {
  if (!iso) return 'none';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function fmtAge(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 48) return `${Math.floor(h / 24)}d ago`;
  if (h > 0) return `${h}h ${m}m ago`;
  return `${m}m ago`;
}

const VERDICT_META = {
  healthy: {
    color: 'bg-green-600 hover:bg-green-700 text-white',
    icon: CheckCircle2,
    label: 'Healthy',
    banner: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900 text-green-800 dark:text-green-300',
  },
  silent_from_vendor: {
    color: 'border-amber-500 text-amber-600 dark:text-amber-400',
    icon: AlertTriangle,
    label: 'Silent from vendor',
    banner: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300',
    outline: true,
  },
  processing_broken: {
    color: 'bg-red-600 hover:bg-red-700 text-white',
    icon: XCircle,
    label: 'Processing broken',
    banner: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300',
  },
  auto_disabled: {
    color: 'bg-red-600 hover:bg-red-700 text-white',
    icon: XCircle,
    label: 'Auto-disabled',
    banner: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300',
  },
  url_mismatch: {
    color: 'bg-red-600 hover:bg-red-700 text-white',
    icon: XCircle,
    label: 'URL mismatch',
    banner: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300',
  },
  signature_rejecting: {
    color: 'bg-red-600 hover:bg-red-700 text-white',
    icon: ShieldAlert,
    label: 'Signature rejecting',
    banner: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300',
  },
  delivery_stopped: {
    color: 'bg-red-600 hover:bg-red-700 text-white',
    icon: WifiOff,
    label: 'Delivery stopped',
    banner: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300',
  },
  subscription_unknown: {
    color: 'border-amber-500 text-amber-600 dark:text-amber-400',
    icon: HelpCircle,
    label: 'Subscription unknown',
    banner: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300',
    outline: true,
  },
};

function VerdictBadge({ verdict }) {
  const meta = VERDICT_META[verdict] || VERDICT_META.silent_from_vendor;
  const Icon = meta.icon;
  if (meta.outline) {
    return (
      <Badge variant="outline" className={`${meta.color} gap-1 text-sm px-3 py-1`}>
        <Icon className="h-4 w-4" />
        {meta.label}
      </Badge>
    );
  }
  return (
    <Badge className={`${meta.color} gap-1 text-sm px-3 py-1`}>
      <Icon className="h-4 w-4" />
      {meta.label}
    </Badge>
  );
}

function VerdictBanner({ verdict, note }) {
  const meta = VERDICT_META[verdict] || {};
  return (
    <div className={`rounded-lg border px-4 py-3 space-y-1 ${meta.banner || ''}`}>
      <div className="flex items-center gap-2">
        <VerdictBadge verdict={verdict} />
      </div>
      {note && <p className="text-sm">{note}</p>}
    </div>
  );
}

function CountRow({ label, value, warn }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums font-medium ${warn ? 'text-red-600 dark:text-red-400' : ''}`}>
        {value == null ? '-' : Number(value).toLocaleString('en-GB')}
      </span>
    </div>
  );
}

function TimestampRow({ label, iso }) {
  const age = fmtAge(iso);
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">
        {fmtDate(iso)}
        {age && <span className="text-muted-foreground ml-2 text-xs">({age})</span>}
      </span>
    </div>
  );
}

function HookRow({ hook }) {
  const hostMismatch = hook.host_mismatch;
  return (
    <div className="border rounded px-3 py-2 space-y-1 text-xs">
      <div className="flex justify-between items-center gap-2">
        <span className="font-mono text-muted-foreground">#{hook.id}</span>
        <span>
          {hook.enabled == null ? (
            <span className="text-muted-foreground">unknown</span>
          ) : hook.enabled ? (
            <Badge className="bg-green-600 text-white text-[10px] px-1.5 py-0">enabled</Badge>
          ) : (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">disabled</Badge>
          )}
        </span>
      </div>
      <div className="flex gap-1 items-baseline">
        <span className="text-muted-foreground shrink-0">URL:</span>
        <span className={`font-mono truncate ${hostMismatch ? 'text-red-600 dark:text-red-400' : ''}`}>
          {hook.target_url || '-'}
        </span>
        {hostMismatch && (
          <Badge variant="destructive" className="text-[10px] px-1.5 py-0 shrink-0">host mismatch</Badge>
        )}
      </div>
      {hook.event && (
        <div className="flex gap-1">
          <span className="text-muted-foreground">Event:</span>
          <span className="font-mono">{hook.event}</span>
        </div>
      )}
      <div className="flex justify-between text-muted-foreground">
        <span>Delivery: {fmtDate(hook.last_delivery_at)}</span>
        <span>Failure: {fmtDate(hook.last_failure_at)}</span>
      </div>
    </div>
  );
}

function HooksBlock({ allHooks, subscription, subscriptionError, publicReceiverHost, publicHostValid, servedHost }) {
  if (subscriptionError) {
    return (
      <div className="text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1 font-mono">
        Subscription lookup failed: {subscriptionError}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">Registration target</span>
        <span className={`font-mono ${publicHostValid === false ? 'text-red-600 dark:text-red-400' : ''}`}>
          {publicReceiverHost || '(not set)'}
        </span>
      </div>
      {publicHostValid === false && (
        <p className="text-[10px] text-red-600 dark:text-red-400">
          UPLISTING_PUBLIC_RECEIVER_HOST is unset or matches an internal pattern. Re-register is disabled.
        </p>
      )}
      {servedHost && (
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Served host <span className="italic">(cross-check)</span></span>
          <span className={`font-mono ${servedHost !== publicReceiverHost ? 'text-muted-foreground' : ''}`}>
            {servedHost}
          </span>
        </div>
      )}
      {allHooks && allHooks.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">{allHooks.length} hook(s) registered:</p>
          {allHooks.map((hook) => (
            <HookRow key={hook.id} hook={hook} />
          ))}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic">
          {subscription?.note || 'No hooks registered on Uplisting'}
        </div>
      )}
    </div>
  );
}

function ReregisterResult({ data }) {
  if (!data) return null;
  const { results, registration_url_masked, hooks_scanned, finance_hooks_found, public_receiver_host } = data;
  return (
    <div className="space-y-2 mt-3">
      <p className="text-xs text-muted-foreground">
        Scanned {hooks_scanned} hook(s), {finance_hooks_found} finance hook(s) found.
        {public_receiver_host && <> Target: <span className="font-mono">{public_receiver_host}</span></>}
      </p>
      {results.length === 0 && (
        <p className="text-xs italic text-muted-foreground">No finance hooks to update.</p>
      )}
      {results.map((r, i) => (
        <div key={i} className="border rounded px-3 py-2 space-y-1 text-xs">
          <div className="flex justify-between items-center">
            <span className="font-mono text-muted-foreground">#{r.hook_id} ({r.event})</span>
            <Badge
              className={`text-[10px] px-1.5 py-0 ${
                ['updated', 'created', 'dedup_removed', 'old_removed'].includes(r.status) ? 'bg-green-600 text-white' :
                r.status === 'no_change' ? 'bg-gray-500 text-white' :
                'bg-red-600 text-white'
              }`}
            >
              {r.status}
            </Badge>
          </div>
          {r.status !== 'no_change' && (
            <>
              <div className="flex gap-1">
                <span className="text-muted-foreground shrink-0">Before:</span>
                <span className="font-mono truncate">{r.before_url}</span>
              </div>
              <div className="flex gap-1">
                <span className="text-muted-foreground shrink-0">After:</span>
                <span className="font-mono truncate">{r.after_url}</span>
              </div>
            </>
          )}
          {r.error && (
            <p className="text-red-600 dark:text-red-400">{r.error}</p>
          )}
        </div>
      ))}
      {registration_url_masked && (
        <div className="flex gap-1 text-xs">
          <span className="text-muted-foreground shrink-0">Registration URL:</span>
          <span className="font-mono truncate">{registration_url_masked}</span>
        </div>
      )}
    </div>
  );
}

export function UplistingWebhookHealthPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [reregLoading, setReregLoading] = useState(false);
  const [reregResult, setReregResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.uplistingWebhookHealth();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Uplisting Webhook Health</CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Are Uplisting webhooks arriving, arriving but failing, or not being sent?
          Read-only, no state is modified.
        </p>
      </CardHeader>

      {result && (
        <CardContent className="space-y-5">
          {/* Verdict banner */}
          <VerdictBanner verdict={result.verdict} note={result.verdict_note} />

          {/* Timestamps */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Timestamps</h3>
            <div className="space-y-1">
              <TimestampRow label="Last inbound (webhook log)" iso={result.last_inbound_at} />
              <TimestampRow label="Last webhook-processed reservation" iso={result.last_webhook_at} />
            </div>
          </div>

          {/* Inbound counts */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Inbound events (signature-verified)</h3>
            <div className="space-y-1">
              <CountRow label="Last 1 hour" value={result.inbound_count_1h} />
              <CountRow label="Last 24 hours" value={result.inbound_count_24h} />
              <CountRow label="Last 7 days" value={result.inbound_count_7d} />
            </div>
          </div>

          {/* Signature failures */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Signature failures (401 rejections)</h3>
            <div className="space-y-1">
              <CountRow label="Last 1 hour" value={result.signature_failure_count_1h} warn={result.signature_failure_count_1h > 0} />
              <CountRow label="Last 24 hours" value={result.signature_failure_count_24h} warn={result.signature_failure_count_24h > 0} />
            </div>
          </div>

          {/* Reservation processing split */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Reservation processing (last 24 h)</h3>
            <div className="space-y-1">
              <CountRow label="Webhook-ingested" value={result.webhook_ingested_24h} />
              <CountRow
                label="Pull-refreshed (masking)"
                value={result.pull_refreshed_24h}
                warn={result.pull_refreshed_24h > 0 && result.webhook_ingested_24h === 0}
              />
              <CountRow label="Failed / quarantined" value={result.failed_count_24h} />
            </div>
            {result.pull_refreshed_24h > 0 && result.webhook_ingested_24h === 0 && (
              <p className="text-[10px] text-red-600 dark:text-red-400 mt-1">
                Pull job is refreshing webhook-originated reservations,
                masking the absence of actual webhook deliveries.
              </p>
            )}
          </div>

          {/* All hooks */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Uplisting hooks</h3>
            <HooksBlock
              allHooks={result.all_hooks}
              subscription={result.subscription}
              subscriptionError={result.subscription_error}
              publicReceiverHost={result.public_receiver_host}
              publicHostValid={result.public_host_valid}
              servedHost={result.served_host}
            />
            {result.url_mismatch_note && (
              <p className="text-[10px] text-muted-foreground mt-1 italic">
                {result.url_mismatch_note}
              </p>
            )}
            <div className="mt-3 pt-3 border-t">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Re-register finance hooks with token URL
                </p>
                <Button
                  onClick={async () => {
                    setReregLoading(true);
                    setReregResult(null);
                    try {
                      const { data } = await diagnosticsAPI.reregisterUplistingHooks();
                      setReregResult(data);
                      const successes = (data.results || []).filter(
                        r => ['created', 'updated', 'dedup_removed', 'old_removed'].includes(r.status)
                      ).length;
                      if (successes > 0) {
                        toast.success(`${successes} hook action(s) completed`);
                      } else {
                        toast.info('No hooks needed updating');
                      }
                    } catch (err) {
                      toast.error(getErrorMessage(err));
                    } finally {
                      setReregLoading(false);
                    }
                  }}
                  disabled={reregLoading || result?.public_host_valid === false}
                  size="sm"
                  variant="outline"
                >
                  {reregLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Re-register
                </Button>
              </div>
              <ReregisterResult data={reregResult} />
            </div>
          </div>

          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked {fmtDate(result.checked_at)}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
